import { useCallback, useEffect, useLayoutEffect, useState } from "react";

const DEFAULT_DROPDOWN_WIDTH = 300;
export const MOBILE_SIDE_OFFSET = 16;

/**
 * Open state of a dropdown and its width: the control's width,
 * on a phone the screen width minus the side offsets.
 *
 * @param {object} params
 * @param {{ current: Element|null }} params.anchorRef - control the dropdown is under
 * @param {boolean} params.isMobile
 * @param {boolean} params.disabled
 * @param {(isOpen: boolean) => void} [params.onOpenChange]
 */
const useDropdownState = ({ anchorRef, isMobile, disabled, onOpenChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownWidth, setDropdownWidth] = useState(DEFAULT_DROPDOWN_WIDTH);

  const changeOpenState = useCallback(
    (nextOpen) => {
      setIsOpen((currentOpen) => {
        const resolvedOpen = typeof nextOpen === "function" ? nextOpen(currentOpen) : nextOpen;
        onOpenChange?.(resolvedOpen);
        return resolvedOpen;
      });
    },
    [onOpenChange],
  );

  const updateDropdownWidth = useCallback(() => {
    if (!anchorRef.current) {
      return;
    }

    if (isMobile) {
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      setDropdownWidth(Math.max(0, viewportWidth - MOBILE_SIDE_OFFSET * 2));
      return;
    }

    setDropdownWidth(anchorRef.current.getBoundingClientRect().width);
  }, [anchorRef, isMobile]);

  const open = useCallback(() => {
    if (!disabled) {
      changeOpenState(true);
    }
  }, [changeOpenState, disabled]);

  const close = useCallback(() => changeOpenState(false), [changeOpenState]);

  const toggle = useCallback(() => {
    if (!disabled) {
      changeOpenState((currentOpen) => !currentOpen);
    }
  }, [changeOpenState, disabled]);

  // Before paint, so the dropdown does not appear with the old width
  useLayoutEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    updateDropdownWidth();
    window.addEventListener("resize", updateDropdownWidth);

    return () => window.removeEventListener("resize", updateDropdownWidth);
  }, [isOpen, updateDropdownWidth]);

  useEffect(() => {
    if (disabled) {
      close();
    }
  }, [close, disabled]);

  return { isOpen, dropdownWidth, open, close, toggle };
};

export default useDropdownState;
